import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { 
  MoreHorizontalIcon
} from "lucide-react";
import moment from "moment";
import { ApplicationAction } from "../../model_gen/application";
import { ApplicationService } from "../../services/ApplicationService";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../ui/paginated-table";

interface IntentsTableProps {
  intents: ApplicationAction[];
  loading: boolean;
  sortConfig: SortConfig<ApplicationAction>;
  onSort: (key: keyof ApplicationAction) => void;
  onIntentClick: (intent: ApplicationAction) => void;
  onRefresh: () => void;
  apiKey: string;
  setIsLoading: (loading: boolean) => void;
}

const IntentsTable: React.FC<IntentsTableProps> = ({
  intents,
  loading,
  sortConfig,
  onSort,
  onIntentClick,
  onRefresh,
  apiKey,
  setIsLoading,
}) => {
  const navigate = useNavigate();

  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const handleDeleteIntent = async (intent: ApplicationAction) => {
    if (!intent.application_id || !intent.id) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete the intent "${intent.action_type}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      setIsLoading(true);
      await ApplicationService.deleteApplicationAction(
        intent.application_id,
        intent.id,
        setIsLoading,
        apiKey
      ).toPromise();
      onRefresh();
    } catch (error) {
      console.error("Error deleting intent:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewIntent = (intent: ApplicationAction) => {
    navigate(`/application/${intent.application_id}/intent/${intent.id}`);
  };

  const columns: PaginatedTableColumn<ApplicationAction>[] = [
    {
      key: "action_type",
      header: "Intent Name",
      width: "w-[35%]",
      sortable: true,
      render: (intent) => (
        <div className="text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors">
          {intent.action_type}
        </div>
      ),
    },
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[20%]",
      sortable: true,
      render: (intent) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(intent.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(intent.date_created)}
          </div>
        </div>
      ),
    },
    {
      key: "last_activity",
      header: "Last Activity",
      width: "w-[20%]",
      sortable: true,
      render: (intent) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {intent.last_activity > 0 ? formatDate(intent.last_activity) : "Never"}
          </div>
          {intent.last_activity > 0 && (
            <div className="text-xs text-muted-foreground">
              {formatTime(intent.last_activity)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "request_count",
      header: "Requests",
      width: "w-[15%]",
      render: (intent) => (
        <span className="text-sm font-gc-med text-foreground">
          {intent.request_count || 0}
        </span>
      ),
    },
    {
      key: "id",
      header: "Actions",
      width: "w-[10%]",
      render: (intent) => (
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              // Simple dropdown menu using native browser context menu
              const menu = document.createElement("div");
              menu.className = "fixed z-[9999] min-w-48 bg-popover border border-border rounded-md shadow-lg py-1";
              menu.innerHTML = `
                <button class="flex items-center w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors">
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                  View Details
                </button>
                <button class="flex items-center w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors text-red-600">
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                  Delete
                </button>
              `;
              
              // Calculate position relative to viewport
              const buttonRect = e.currentTarget.getBoundingClientRect();
              const menuWidth = 192; // min-w-48 = 12rem = 192px
              const menuHeight = 80; // approximate height for 2 buttons
              
              // Position to the right of the button, but adjust if it would go off-screen
              let left = buttonRect.right + 8;
              let top = buttonRect.top;
              
              // Adjust if menu would go off the right edge
              if (left + menuWidth > window.innerWidth) {
                left = buttonRect.left - menuWidth - 8;
              }
              
              // Adjust if menu would go off the bottom edge
              if (top + menuHeight > window.innerHeight) {
                top = buttonRect.bottom - menuHeight;
              }
              
              menu.style.left = `${left}px`;
              menu.style.top = `${top}px`;
              
              // Append to body to avoid clipping
              document.body.appendChild(menu);
              
              // Add click handlers
              const buttons = menu.querySelectorAll("button");
              buttons[0].onclick = (evt) => {
                evt.stopPropagation();
                handleViewIntent(intent);
                menu.remove();
              };
              buttons[1].onclick = (evt) => {
                evt.stopPropagation();
                handleDeleteIntent(intent);
                menu.remove();
              };
              
              // Remove menu when clicking outside
              const handleClickOutside = (evt: Event) => {
                if (!menu.contains(evt.target as Node)) {
                  menu.remove();
                  document.removeEventListener("click", handleClickOutside);
                }
              };
              setTimeout(() => document.addEventListener("click", handleClickOutside), 0);
            }}
          >
            <MoreHorizontalIcon className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PaginatedTable<ApplicationAction>
      data={intents}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={1}
      onPageChange={() => {}}
      pageSize={intents.length}
      showPagination={false}
      emptyTitle="No intents found"
      emptyDescription="Create your first intent to get started."
      onRowClick={onIntentClick}
    />
  );
};

export default IntentsTable; 